import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { WorkflowService } from '../../../../../../../shared/services/workflow.service';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../state/app.state';
import { ActivatedRoute, Router } from '@angular/router';
import { dailyReportsByDate, siteDetails } from '../../../../../core/projects.selectors';
import { takeUntil } from 'rxjs/operators';
import { DeleteDailyReportRequest, ExecuteCurrentActionStateRequest } from '../../../../../core/projects.actions';
import { FormControl, FormGroup } from '@angular/forms';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../shared/components';
import { ProjectsService } from '../../../../../core/projects.service';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { CopyDailyReportComponent } from '../../../components/copy-daily-report/copy-daily-report.component';

@Component({
    selector: 'app-daily-reports-list',
    templateUrl: './daily-reports-list.component.html',
    styleUrls: ['./daily-reports-list.component.scss'],
    standalone: false
})
export class DailyReportsListComponent implements OnInit, OnDestroy {
  @Input() selectedReportId = '';
  dailyReportsList: any[] = [];
  filteredDailyReportsList: any[] = [];
  site: any = {};
  projectId: any;
  siteId: any;
  form: FormGroup = new FormGroup({});
  formInputInfo: any;
  reportId: any;
  date: any;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private projectsService: ProjectsService,
    public dialog: MatDialog,
    public workflowService: WorkflowService,
  ) {
    this.route.params.subscribe(params => {
      this.projectId = params['projectId'];
      this.siteId = params['siteId'];
    });
    this.route.queryParams.subscribe(params => {
      this.reportId = params['reportId'];
      if (params.date && moment(params.date, 'YYYY-MM-DD').isValid()) {
        this.date = params.date;
      }
    });
  }

  ngOnInit() {
    this.store.select(dailyReportsByDate)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(dailyReports => {
        this.dailyReportsList = dailyReports;
        this.filterReportsList();
      });

    this.store.select(siteDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(site => {
        this.site = site;
        this.filterReportsList();
      });
  }

  filterReportsList() {
    if (this.site.id && this.dailyReportsList.length > 0) {
      this.filteredDailyReportsList = this.dailyReportsList.filter(o => o.site_id === this.site.id);
    } else {
      this.filteredDailyReportsList = [];
    }
    if (!this.selectedReportId && this.filteredDailyReportsList.length > 0) {
      this.selectReportId(this.filteredDailyReportsList[0].id);
    }

    const firstAction = this.filteredDailyReportsList?.[0]?.['next-actions']?.[0]?.actions?.[0];
    this.formInputInfo = firstAction?.options?.formInputs?.[0];
    this.form.addControl(`${this.formInputInfo?.name}`, new FormControl({ value: '', disabled: false }));
  }

  selectReportId(reportId) {
    if (this.selectedReportId === reportId) {
      reportId = null;
    }

    this.router.navigate([], {
      queryParams: {
        reportId,
        expandedSection: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  executeState(action) {
    const { wf, ex, state } = action;
    const qp = { wf, ex, state, include: '' };
    const formRawValues = this.form.getRawValue();
    const formValues = formRawValues[this.formInputInfo?.name];

    const payload = {
      executeCurrentActionStatePayload: {
        qp,
        input: {
          formValues,
          id: this.reportId,
        },
      },
      fetchNextActionsStatePayload: {
        qp: {
          wf,
          project_id: this.projectId,
          site_id: this.siteId,
          id: this.reportId,
        },
      },
    };

    const isFormDirty = this.projectsService.isAnyFormDirty.getValue();

    if (isFormDirty) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        disableClose: true,
        data: new ConfirmDialogModel(
          'Unsaved changes',
          `There are unsaved changes.<br/>Are you sure you want to discard?`,
        ),
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.store.dispatch(ExecuteCurrentActionStateRequest({ payload }));
          this.projectsService.isAnyFormDirty.next(false);
        }
      });
    } else {
      this.store.dispatch(ExecuteCurrentActionStateRequest({ payload }));
    }
  }

  delete(report) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete',
        `Are you sure you want to delete the selected report?`,
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const payload = {
          reportId: report?.id,
          projectId: this.projectId,
          siteId: report?.site_id,
          date: this.date,
        };
        this.store.dispatch(DeleteDailyReportRequest({ payload }));
      }
    });
  }

  copyTo(report: any) {
    this.dialog.open(CopyDailyReportComponent, {
      disableClose: false,
      data: { x: 3, report: { ...report, projectId: this.projectId } },
      panelClass: 'floating-dialog-box',
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
